import { useCallback } from 'react';
import ICursorable from 'types/Cursorable';
import { getLastCursor } from 'utils/apolloUtils';
import { logError } from 'utils/testUtils';
import { ILazyLoadingState } from './useLazyLoading/useLazyLoading';

export interface IUsePagedCursorLoaderPayload<T extends ICursorable> {
  data: T[];
  loadedAll: boolean;
  error: boolean;
}

export default function usePagedCursorLoader<T extends ICursorable>(
  state: Omit<ILazyLoadingState<T>, 'loading'>,
  getData: (after: string | null, first: number) => Promise<T[]>,
  sectionSize: number = 20
) {
  const getDataPayload = useCallback(
    async (cursor: string | null): Promise<IUsePagedCursorLoaderPayload<T>> => {
      try {
        const result = await getData(cursor ?? null, sectionSize);
        return {
          data: [...state.data, ...result],
          loadedAll: result.length < sectionSize,
          error: false,
        };
      } catch (e) {
        // `.catch` because logError returns a rejected promise; left un-awaited it would surface as
        // an unhandled rejection.
        logError(e).catch(() => undefined);
        return { data: state.data, loadedAll: true, error: true };
      }
    },
    [getData, sectionSize, state.data]
  );

  const getMoreData = useCallback(async (): Promise<IUsePagedCursorLoaderPayload<T>> => {
    try {
      return getDataPayload(getLastCursor(state.data));
    } catch (e: unknown) {
      logError(e).catch(() => undefined);
      return {
        data: state.data,
        error: true,
        loadedAll: true,
      };
    }
  }, [getDataPayload, state.data]);

  const loadAll = useCallback(async (): Promise<IUsePagedCursorLoaderPayload<T>> => {
    if (state.loadedAll) {
      return { loadedAll: state.loadedAll, error: state.error, data: state.data };
    } else {
      // Accumulate pages ourselves rather than via getDataPayload, which always re-prefixes the
      // render-time `state.data` and would therefore discard every page gathered in this loop.
      const accumulated = [...state.data];
      let loadedAll = false;
      try {
        do {
          const result = await getData(getLastCursor(accumulated), sectionSize);
          accumulated.push(...result);
          loadedAll = result.length < sectionSize;
        } while (!loadedAll);

        return { data: accumulated, loadedAll, error: state.error };
      } catch (e) {
        // `.catch` because logError returns a rejected promise; left un-awaited it would surface as
        // an unhandled rejection.
        logError(e).catch(() => undefined);
        return { data: accumulated, loadedAll: true, error: true };
      }
    }
  }, [getData, sectionSize, state.data, state.error, state.loadedAll]);

  return { getMoreData, loadAll };
}
